import { useMemo } from 'react';
import { CurrencyDisplayScenario } from 'commons/enums/CurrencyDisplayScenario.enum';
import { Currency } from 'commons/types/Currency.enum';
import { formatCurrency } from 'commons/utils';
import { IStoreInfo } from 'redux/slices/types';
import { useIsForeignTransaction } from './useIsForeignTransaction';

interface UseCurrencyDisplayProps {
  currency: Currency;
  exchangeRate?: number;
  value: number;
  storeInfo: IStoreInfo;
  showWithExchangeRate?: boolean;
  showTooltip?: boolean;
}

interface UseCurrencyDisplayReturn {
  isForeign: boolean;
  displayValue: string;
  tooltipContent: string | undefined;
}

const {
  LOCAL,
  FOREIGN_WITH_EXCHANGE_RATE,
  FOREIGN_WITH_TOOLTIP,
  FOREIGN_WITHOUT_TOOLTIP,
} = CurrencyDisplayScenario;

export const useCurrencyDisplay = ({
  currency,
  exchangeRate,
  value,
  storeInfo,
  showWithExchangeRate = false,
  showTooltip = false,
}: UseCurrencyDisplayProps): UseCurrencyDisplayReturn => {
  const { isForeignTransaction, localCurrency } = useIsForeignTransaction({
    storeCountry: storeInfo.country,
  });

  const isForeign = isForeignTransaction(currency, exchangeRate || 0);

  const { displayValue, tooltipContent } = useMemo(() => {
    const getScenario = (): CurrencyDisplayScenario => {
      if (!isForeign) return LOCAL;
      if (showWithExchangeRate) return FOREIGN_WITH_EXCHANGE_RATE;
      return showTooltip ? FOREIGN_WITH_TOOLTIP : FOREIGN_WITHOUT_TOOLTIP;
    };

    const scenarioHandlers = {
      [LOCAL]: () => ({
        displayValue: formatCurrency({ value, currency }, storeInfo),
        tooltipContent: undefined,
      }),

      [FOREIGN_WITH_EXCHANGE_RATE]: () => {
        const erFormatted = formatCurrency(
          { value: exchangeRate!, currency: localCurrency },
          storeInfo,
        );
        return {
          displayValue: formatCurrency({ value, currency }, storeInfo),
          tooltipContent: `${currency} 1 = ${erFormatted}`,
        };
      },

      [FOREIGN_WITH_TOOLTIP]: () => ({
        displayValue: formatCurrency(
          { value: value * exchangeRate!, currency: localCurrency },
          storeInfo,
        ),
        tooltipContent: formatCurrency({ value, currency }, storeInfo),
      }),

      [FOREIGN_WITHOUT_TOOLTIP]: () => ({
        displayValue: formatCurrency(
          { value: value * exchangeRate!, currency: localCurrency },
          storeInfo,
        ),
        tooltipContent: undefined,
      }),
    };

    const scenario = getScenario();
    return scenarioHandlers[scenario]();
  }, [
    currency,
    exchangeRate,
    value,
    localCurrency,
    showWithExchangeRate,
    isForeign,
    storeInfo,
    showTooltip,
  ]);

  return {
    isForeign,
    displayValue,
    tooltipContent,
  };
};
